import * as React from "react";
import { useMappedState } from "redux-react-hook";
import styled, { css } from "styled-components";
import { FormikValues } from "formik";
import _ from "lodash";
import { toast } from "react-toastify";

import Grid from "src/library/components/Hedron";
import {
  Form,
  IFormField
} from "src/library/components/Forms";
import { Text } from "src/library/components/Text";
import { Logo } from "src/core/shared/components/Logo";
import type { IStore } from "src/core/shared/store";
import { Button } from "src/library/components/Button";
import {
  ImportOrigin,
  ImportSettingType,
  useImportSettingApi
} from "src/core/shared/api/importSetting.api";
import { usePendingCustomerApi } from "src/core/pending-customers/api/pendingCustomer.api";
import useGenerateCustomerFormFields from "src/core/pending-customers/hooks/useGenerateCustomerFormFields";

/** Renders the customer registration page. This page is public. */
const RegistrationPage: React.FC = () => {
  const { globalPlatformSettings, allowPendingCustomers } = useMappedState(({ global }: IStore) => ({
    globalPlatformSettings: global.settings && global.settings.platform,
    allowPendingCustomers: global.settings?.allowPendingCustomers
  }));

  const [ newCustomerImportSettingRes, newCustomerImportSettingReq ] = useImportSettingApi("GET_IMPORT_SETTING");
  const [ registerCustomerRes, registerCustomerReq ] = usePendingCustomerApi("CREATE_PENDING_CUSTOMER");
  const [ formFields, setFormFields ] = React.useState<IFormField[]>([]);
  const [ registrationComplete, setRegistrationComplete ] = React.useState(false);
  const [ error, setError ] = React.useState<string | null>(null);
  const { generateFormFields } = useGenerateCustomerFormFields();

  React.useEffect(() => {
    newCustomerImportSettingReq({
      type: ImportSettingType.PendingCustomer,
      origin: ImportOrigin.FORM
    });
  }, [ newCustomerImportSettingReq ]);

  React.useEffect(() => {
    if (newCustomerImportSettingRes.data) {
      const fields = generateFormFields(newCustomerImportSettingRes.data);

      setFormFields(fields);
    }
  }, [ generateFormFields, newCustomerImportSettingRes.data ]);

  React.useEffect(() => {
    if (newCustomerImportSettingRes.error) {
      setError(newCustomerImportSettingRes.error.message);
    }
  }, [ newCustomerImportSettingRes.error ]);

  React.useEffect(() => {
    if (registerCustomerRes.data) {
      setRegistrationComplete(true);
    }

    if (registerCustomerRes.error) {
      toast.error(registerCustomerRes.error.message);
    }
  }, [ registerCustomerRes.data, registerCustomerRes.error ]);

  const handleSubmit = React.useCallback((fields: FormikValues) => {
    registerCustomerReq({
      name: fields.Name,
      address: {
        addressLine1: fields.AddressLine1,
        addressLine2: fields.AddressLine2,
        addressLine3: fields.AddressLine3,
        city: fields.City,
        postcode: fields.PostCode
      },
      contactEmail: fields.ContactEmail,
      contactName: fields.ContactName,
      contactNumber: fields.ContactNumber,
      customData: { ...fields }
    });
  }, [ registerCustomerReq ]);

  if (!allowPendingCustomers) {
    return null;
  }

  return (
    <StyledWrapper registrationComplete={registrationComplete}>
      <div className="inner-content">
        <Grid.Box className="logo-wrapper">
          <Logo type="dark" />
        </Grid.Box>

        <Grid.Box>
          <Text
            fontSize="h2"
            color="secondary"
          >
            {registrationComplete ? "Registration Complete!" : "Register for portal access."}
          </Text>
        </Grid.Box>

        {registrationComplete ? (
          <div className="registration-complete-text">
            <Grid.Box>
              <Text as="p">
                Thank you for registering to use our online ordering platform.
              </Text>

              <Text as="p">
                A member of the team will be in touch shortly.
              </Text>
            </Grid.Box>

            <Button
              $buttonType="primary"
              onClick={() => {
                setRegistrationComplete(false);
              }}
            >
              Restart Registration
            </Button>

          </div>
        ) : (
          <>
            {globalPlatformSettings && globalPlatformSettings.maintenance_mode.roles.length > 0 && (
              <Grid.Box style={{ paddingTop: "14px" }}>
                <Text
                  fontSize="h4"
                  color="secondary"
                >
                  System in maintenance mode
                </Text>
              </Grid.Box>
            )}

            <Grid.Box className="form-wrapper">
              <Form
                error={error}
                submitting={registerCustomerRes.pending}
                fields={formFields}
                onSubmit={fields => handleSubmit(fields)}
                submitButtonText="Submit Account Request"
                submitButtonAlignment="center"
              />
            </Grid.Box>
          </>
        )}

      </div>
    </StyledWrapper>
  );
};

export const StyledWrapper = styled.div<{registrationComplete: boolean}>`
  ${({ theme, registrationComplete }) => css`
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: flex-start;
    overflow-y: scroll;

    ${registrationComplete ? "height: 100vh;" : ""}

    .inner-content {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      width: 100%;
      padding: ${theme.spacing.padding.xl};
      background-color: ${theme.colors.white};
      margin: 50px 0;
      box-shadow: 0 0 12px 0 rgba(0, 0, 0, 0.4);
      border-radius: ${theme.borderRadius};

      @media (min-width: 600px) {
        width: 500px;
      }
  
      .logo-wrapper {
        max-width: 200px;
        margin-bottom: 30px;
      }
  
      .form-wrapper {
        width: 100%;
        display: flex;
        justify-content: center;
        margin-top: ${({ theme }) => theme.spacing.margins.xl};
      
        form {
          width: 100%;
          max-width: 400px;
        }
      }

      .registration-complete-text {
        display: flex;
        flex-direction: column;
        align-items: center;
        text-align: center;
        justify-content: center;
        padding-top: ${theme.spacing.padding.lg};
        
        .button {
          margin-top: ${theme.spacing.margins.lg};
        }
      }
    }
  `}
`;

export default RegistrationPage;
